/**
 * The whole router, which is deliberately about forty lines.
 *
 * A station tablet needs deep links that survive a reload and a back button that
 * works. It does not need nested layouts, loaders, lazy boundaries or a data
 * router, and adding a dependency for URL matching would be the largest thing in
 * this bundle for the least reason. `history.pushState` plus `popstate` is the
 * whole mechanism.
 *
 * Paths are matched by exact string or by a single `:param` segment - the app has
 * no route deeper than that, and a matcher that cannot express more is a matcher
 * that cannot be quietly grown into a framework.
 */
import { useCallback, useEffect, useState } from 'react'

export function currentPath(): string {
  if (typeof window === 'undefined') return '/'
  return window.location.pathname || '/'
}

export function navigate(to: string): void {
  if (to === currentPath()) return
  window.history.pushState(null, '', to)
  // pushState does not fire popstate, so the subscribers are told directly.
  window.dispatchEvent(new PopStateEvent('popstate'))
}

/** The current path, re-rendering on back, forward and `navigate`. */
export function usePath(): string {
  const [path, setPath] = useState(currentPath)
  useEffect(() => {
    const onPop = () => setPath(currentPath())
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])
  return path
}

export function useNavigate(): (to: string) => void {
  return useCallback((to: string) => navigate(to), [])
}

/**
 * Matches `pattern` against `path`, returning the captured params or null.
 * `/shop/listings/:id` against `/shop/listings/abc` gives `{id: 'abc'}`.
 */
export function match(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean)
  const a = path.split('/').filter(Boolean)
  if (p.length !== a.length) return null
  const params: Record<string, string> = {}
  for (let i = 0; i < p.length; i++) {
    const seg = p[i] as string
    const got = a[i] as string
    if (seg.startsWith(':')) {
      params[seg.slice(1)] = decodeURIComponent(got)
      continue
    }
    if (seg !== got) return null
  }
  return params
}
